import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Select,
  MenuItem,
  Button,
  Divider,
  TextField,
} from "@mui/material";

import StoreGroupApi from "../api/StoreGroupApi"; // Import the API

const divisions = ["mens", "womens"];
const groups = ["A", "B", "C", "D", "E", "F"];

const ManageGroups = () => {
  const [stores, setStores] = useState([]); // List of stores
  const [storeGroupings, setStoreGroupings] = useState({}); // Grouping data
  const [updatedGroupings, setUpdatedGroupings] = useState({}); // Track updates
  const [searchTerm, setSearchTerm] = useState(""); // Search term
  const [filteredStores, setFilteredStores] = useState([]); // Filtered stores

  useEffect(() => {
    const fetchStoresAndGroupings = async () => {
      try {
        // Fetch stores
        const storeResponse = await StoreGroupApi.fetchAllStores();
        const nonEcommerceStores = storeResponse.data.filter(
          (store) => store?.STORE_NAME !== "ECOMMERCE"
        );
        setStores(nonEcommerceStores);
        setFilteredStores(nonEcommerceStores); // Initialize filtered stores

        // Fetch groupings
        const groupingResponse = await StoreGroupApi.fetchStoreGroupings();
        const initialGroupings = groupingResponse.data.reduce(
          (acc, { store_id, groups }) => {
            acc[store_id] = groups;
            return acc;
          },
          {}
        );
        setStoreGroupings(initialGroupings);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchStoresAndGroupings();
  }, []);

  const handleGroupChange = (storeId, division, groupType) => {
    // Track changes locally
    setUpdatedGroupings((prev) => ({
      ...prev,
      [`${storeId}-${division}`]: {
        store_id: storeId,
        division,
        group_type: groupType,
      },
    }));

    // Update the displayed state
    setStoreGroupings((prev) => ({
      ...prev,
      [storeId]: {
        ...prev[storeId],
        [division]: groupType,
      },
    }));
  };

  const handleSave = async () => {
    try {
      const payload = Object.values(updatedGroupings);
      if (payload.length > 0) {
        await StoreGroupApi.saveStoreGroupings(payload);
        alert("Groupings saved successfully!");
        setUpdatedGroupings({}); // Clear updates after saving
      } else {
        alert("No changes to save.");
      }
    } catch (error) {
      console.error("Error saving groupings:", error);
      alert("Failed to save groupings.");
    }
  };

  const handleSearch = (e) => {
    const term = e.target.value.toLowerCase();
    setSearchTerm(term);
    setFilteredStores(
      stores.filter((store) =>
        `${store.STORE_NAME || store.name} ${
          storeGroupings[store.id]?.mens || ""
        } ${storeGroupings[store.id]?.womens || ""}`
          .toLowerCase()
          .includes(term)
      )
    );
  };

  return (
    <Box sx={{ padding: 3 }}>
      <Typography variant="h6" sx={{ marginBottom: 1 }}>
        Manage Groups
      </Typography>
      <Divider sx={{ marginY: 2 }} />
      <Box sx={{ marginBottom: 2 }}>
        <TextField
          placeholder="Search by store or group"
          variant="outlined"
          size="small"
          fullWidth
          value={searchTerm}
          onChange={handleSearch}
        />
      </Box>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell style={{ fontWeight: "bold" }}>Store</TableCell>
              {divisions.map((division) => (
                <TableCell key={division} style={{ fontWeight: "bold" }}>
                  {division.toUpperCase()} Group
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredStores.map((store) => (
              <TableRow key={store.id}>
                <TableCell>{store.STORE_NAME || store.name}</TableCell>
                {divisions.map((division) => (
                  <TableCell key={division}>
                    <Select
                      value={storeGroupings[store.id]?.[division] || ""}
                      onChange={(e) =>
                        handleGroupChange(store.id, division, e.target.value)
                      }
                      displayEmpty
                      fullWidth
                      size={"small"}
                    >
                      <MenuItem value="">None</MenuItem>
                      {groups.map((group) => (
                        <MenuItem key={group} value={group}>
                          {`Group ${group}`}
                        </MenuItem>
                      ))}
                    </Select>
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Box sx={{ marginTop: 2, textAlign: "right" }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          sx={{ fontWeight: "bold" }}
        >
          Save Groups
        </Button>
      </Box>
    </Box>
  );
};

export default ManageGroups;
